import React, { useState, useEffect } from 'react';
import { useContract } from '../../../context/ContractContext';
import { formatNumber, formatUserId } from '../../../utils/formatters';
import './BinaryTree.css';

const TreeNode = ({ userId, onNavigate, isRoot = false }) => {
  const { getUserInfo, getBinaryInfo } = useContract();
  const [nodeData, setNodeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId || userId === 0) {
        setNodeData(null);
        setLoading(false);
        return;
      }

      try {
        const [userInfo, binaryInfo] = await Promise.all([
          getUserInfo(userId),
          getBinaryInfo(userId)
        ]);

        setNodeData({
          userId,
          investment: userInfo?.totalInvestment || '0',
          leftBusiness: binaryInfo?.leftBusinessTotal || '0',
          rightBusiness: binaryInfo?.rightBusinessTotal || '0',
          leftChild: binaryInfo?.leftChild || 0,
          rightChild: binaryInfo?.rightChild || 0,
          active: userInfo?.active || false
        });
      } catch (err) {
        console.error('Error fetching node data:', err);
        setNodeData(null);
      }
      setLoading(false);
    };

    fetchData();
  }, [userId]);

  if (loading) {
    return (
      <div className="tree-node loading">
        <div className="node-loading-spinner" />
      </div>
    );
  }

  if (!nodeData) {
    return (
      <div className="tree-node empty">
        <div className="node-empty-icon">+</div>
        <span className="node-empty-text">Empty</span>
      </div>
    );
  }

  return (
    <div 
      className={`tree-node ${isRoot ? 'root' : ''} ${nodeData.active ? 'active' : 'inactive'}`}
      onClick={() => onNavigate && onNavigate(userId)}
    >
      <div className="node-id">{formatUserId(nodeData.userId)}</div>
      <div className="node-investment">{formatNumber(nodeData.investment)}</div>
      <div className="node-business">
        <span className="left">{formatNumber(nodeData.leftBusiness, 0)}</span>
        <span className="divider">/</span>
        <span className="right">{formatNumber(nodeData.rightBusiness, 0)}</span>
      </div>
    </div>
  );
};

const BinaryTree = ({ rootUserId }) => {
  const { getBinaryTree, userId: currentUserId } = useContract();
  const [viewingId, setViewingId] = useState(rootUserId || currentUserId);
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const fetchTree = async () => {
      if (!viewingId) return;
      
      setLoading(true);
      try {
        const treeData = await getBinaryTree(viewingId);
        setTree(treeData);
      } catch (err) {
        console.error('Error fetching tree:', err);
      }
      setLoading(false);
    };

    fetchTree();
  }, [viewingId]);

  const navigateToNode = (nodeId) => {
    if (nodeId && nodeId !== 0 && nodeId !== viewingId) {
      setHistory(prev => [...prev, viewingId]);
      setViewingId(nodeId);
    }
  };

  const goBack = () => {
    if (history.length > 0) {
      const prevId = history[history.length - 1];
      setHistory(prev => prev.slice(0, -1));
      setViewingId(prevId);
    }
  };

  const goToRoot = () => {
    setHistory([]);
    setViewingId(rootUserId || currentUserId);
  };

  if (loading && !tree) {
    return <div className="binary-tree-loading">Loading tree...</div>;
  }

  return (
    <div className="binary-tree">
      <div className="tree-controls">
        <button 
          className="tree-control-btn"
          onClick={goBack}
          disabled={history.length === 0}
        >
          ← Back
        </button>
        <span className="tree-viewing">Viewing: {formatUserId(viewingId)}</span>
        <button 
          className="tree-control-btn"
          onClick={goToRoot}
          disabled={viewingId === (rootUserId || currentUserId)}
        >
          My Tree
        </button>
      </div>

      <div className="tree-container">
        {/* Level 1 - Root */}
        <div className="tree-level level-1">
          <TreeNode userId={viewingId} isRoot onNavigate={navigateToNode} />
        </div>

        {/* Connectors Level 1 to 2 */}
        <div className="tree-connectors">
          <div className="connector-line left" />
          <div className="connector-line right" />
        </div>

        {/* Level 2 */}
        <div className="tree-level level-2">
          <TreeNode userId={tree?.left} onNavigate={navigateToNode} />
          <TreeNode userId={tree?.right} onNavigate={navigateToNode} />
        </div>

        {/* Connectors Level 2 to 3 */}
        <div className="tree-connectors level-2">
          <div className="connector-group">
            <div className="connector-line left" />
            <div className="connector-line right" />
          </div>
          <div className="connector-group">
            <div className="connector-line left" />
            <div className="connector-line right" />
          </div>
        </div>

        {/* Level 3 */}
        <div className="tree-level level-3">
          <TreeNode userId={tree?.leftLeft} onNavigate={navigateToNode} />
          <TreeNode userId={tree?.leftRight} onNavigate={navigateToNode} />
          <TreeNode userId={tree?.rightLeft} onNavigate={navigateToNode} />
          <TreeNode userId={tree?.rightRight} onNavigate={navigateToNode} />
        </div>
      </div>
    </div>
  );
};

export default BinaryTree;
